/**
 * Builds an avatar URL with version embedded in filename
 * @param baseUrl - The base avatar URL (without version)
 * @param version - The avatar version string (short hash)
 * @returns The avatar URL with version embedded in filename
 */
export function buildAvatarUrl(baseUrl: string | null | undefined, version?: string): string | null {
    if (!baseUrl) {
        return null;
    }

    // If no version provided, return the base URL as is
    if (!version) {
        return baseUrl;
    }

    // If the URL already has a version embedded (matches pattern), return as is
    if (baseUrl.match(/\.([a-f0-9]{8,})\.jpg$/)) {
        return baseUrl;
    }

    // Check if URL ends with trailing slash
    const hasTrailingSlash = baseUrl.endsWith('/');
    const cleanBaseUrl = hasTrailingSlash ? baseUrl.slice(0, -1) : baseUrl;

    // Extract the base path and filename
    const urlParts = cleanBaseUrl.split('/');
    const filename = urlParts.pop();

    if (!filename) {
        return baseUrl;
    }

    // Check if it's already a versioned filename
    if (filename.match(/\.([a-f0-9]{8,})\.jpg$/)) {
        return baseUrl;
    }

    // Extract user ID from filename (remove .jpg extension)
    const userId = filename.replace(/\.jpg$/, '');

    // Build versioned filename
    const versionedFilename = `${userId}.${version}.jpg`;

    // Reconstruct URL with versioned filename
    urlParts.push(versionedFilename);
    const result = urlParts.join('/');

    // Preserve trailing slash if original URL had one
    return hasTrailingSlash ? result + '/' : result;
}

/**
 * Extracts the base URL from an avatar URL (removes version from filename)
 * @param url - The avatar URL that may contain version in filename
 * @returns The base URL without version
 */
export function extractBaseAvatarUrl(url: string): string {
    // Check if URL has version embedded in filename
    const match = url.match(/^(.*\/)([^\/]+)\.([a-f0-9]{8,})\.jpg$/);
    if (match) {
        const [, basePath, userId] = match;
        return `${basePath}${userId}.jpg`;
    }
    return url;
}

/**
 * Extracts the version string from an avatar URL (from filename)
 * @param url - The avatar URL that may contain version in filename
 * @returns The version string or undefined if not present
 */
export function extractAvatarVersion(url: string): string | undefined {
    const match = url.match(/\.([a-f0-9]{8,})\.jpg$/);
    return match ? match[1] : undefined;
}
